import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { Clock, Mail, LogOut, AlertCircle, Building2, BadgeCheck, CheckCircle2, XCircle, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGetHcpProfileSummary } from "@workspace/api-client-react";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

const REGISTRATION_BODY_LABELS: Record<string, string> = {
  nmc: "NMC (Nursing & Midwifery Council)",
  gmc: "GMC (General Medical Council)",
  hcpc: "HCPC (Health & Care Professions Council)",
  gphc: "GPhC (General Pharmaceutical Council)",
  none: "Not registered",
};

const ROLE_LABELS: Record<string, string> = {
  nurse: "Nurse", doctor: "Pharmacist", pharmacist: "Pharmacist",
  physio: "Physiotherapist", ot: "Occupational Therapist", coordinator: "Coordinator",
};

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-xs font-medium text-slate-500 shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-slate-800 text-right flex items-center gap-1.5">{value}</span>
    </div>
  );
}

export default function PendingApproval({ rejected = false }: { rejected?: boolean }) {
  const { signOut } = useAuth();
  const [rejectionReason, setRejectionReason] = useState<string | null>(null);
  const [skipping, setSkipping] = useState(false);
  const { data: summary } = useGetHcpProfileSummary();

  const handleContinueAsPatient = async () => {
    setSkipping(true);
    try {
      const res = await fetch(`${basePath}/api/user/role`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: "patient" })
      });
      if (res.ok) location.href = `${basePath}/patient`;
    } catch {}
    setSkipping(false);
  };

  useEffect(() => {
    if (!rejected) return;
    fetch(`${basePath}/api/user/profile`, { credentials: "include" })
      .then((res) => { if (res.status === 403) return res.json(); return null; })
      .then((data: { rejectionReason?: string | null } | null) => { if (data?.rejectionReason) setRejectionReason(data.rejectionReason); })
      .catch(() => {});
  }, [rejected]);

  const submittedDate = summary?.createdAt ? new Date(summary.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : null;

  return (
    <div className="min-h-[100dvh] bg-slate-50 flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8"><span className="text-4xl font-bold text-teal-700">RheumHub</span></div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 space-y-6">
          {rejected ? (
            <>
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-50 border border-red-100"><Mail className="h-6 w-6 text-red-500" /></div>
                <div><h1 className="text-xl font-bold text-slate-900 mb-2">Access not granted</h1><p className="text-sm text-slate-500 leading-relaxed">Your account application was not approved at this time.</p></div>
                <a href="mailto:hello@medantixhealth.co.uk" className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-800 underline underline-offset-2"><Mail className="h-4 w-4" />hello@medantixhealth.co.uk</a>
              </div>
              {rejectionReason && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-left"><div className="flex items-start gap-2"><AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" /><div><p className="text-xs font-semibold text-red-700 mb-1">Reason from our team</p><p className="text-sm text-red-800 leading-relaxed">{rejectionReason}</p></div></div></div>}
            </>
          ) : (
            <>
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 border border-amber-100"><Clock className="h-6 w-6 text-amber-500" /></div>
                <div><h1 className="text-xl font-bold text-slate-900 mb-2">Account pending approval</h1><p className="text-sm text-slate-500 leading-relaxed">Thanks for signing up. Your account is being reviewed.</p></div>
              </div>
              <div className="bg-slate-50 rounded-xl border border-slate-200 px-5 py-4 space-y-1.5">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">What happens next</p>
                <ul className="space-y-1.5 text-sm text-slate-500">
                  <li className="flex items-start gap-2"><span className="text-teal-600 font-bold mt-0.5">1.</span><span>Our team reviews your account</span></li>
                  <li className="flex items-start gap-2"><span className="text-teal-600 font-bold mt-0.5">2.</span><span>You'll get an email when approved</span></li>
                  <li className="flex items-start gap-2"><span className="text-teal-600 font-bold mt-0.5">3.</span><span>Sign back in to access RheumHub</span></li>
                </ul>
              </div>
              <p className="text-xs text-slate-400 text-center">Questions? <a href="mailto:hello@medantixhealth.co.uk" className="text-teal-700 underline underline-offset-2 hover:text-teal-800">hello@medantixhealth.co.uk</a></p>

              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-500 mb-2 text-center">If you're a patient, you can skip the review:</p>
                <button onClick={handleContinueAsPatient} disabled={skipping} className="w-full py-2.5 text-sm font-medium rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 transition-colors">
                  {skipping ? "Setting up…" : "Continue as patient"}
                </button>
              </div>
            </>
          )}
          {summary && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 bg-white"><BadgeCheck className="h-4 w-4 text-teal-600 shrink-0" /><span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{rejected ? "Information on file" : "What we received"}</span></div>
              <div className="px-4 divide-y divide-slate-100">
                <SummaryRow label="Role" value={ROLE_LABELS[summary.role] ?? summary.role} />
                <SummaryRow label="Organisation" value={summary.organisationName ? <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />{summary.organisationName}</span> : <span className="text-slate-400 italic">Not provided</span>} />
              </div>
            </div>
          )}
          <div className="flex justify-center">
            <Button variant="ghost" size="sm" onClick={() => signOut()} className="text-slate-500 gap-1.5"><LogOut className="h-4 w-4" />Sign out</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
